import { Box, Card, CardContent, LinearProgress, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import AuthLayout from "layouts/AuthLayout/AuthLayout";
import Logo from "assets/images/side_nav_inkfree_logo.svg";
import { mobile, signer, signerAccentBar } from "theme/colors";
import { authRef, refFont } from "screens/Auth/AuthReferenceStyles";
import { useAppSelector } from "hooks/use-app-select";
import { useAppDispatch } from "hooks/use-app-dispatch";
import {
  logOutAction,
  selectAccessToken,
  selectAuthenticated,
} from "redux/authSlice";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import urls from "global/constants/UrlConstants";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";

const cardSx = {
  width: "100%",
  maxWidth: 460,
  mx: "auto",
  borderRadius: "16px",
  border: `1px solid ${authRef.cardBorder}`,
  boxShadow: authRef.cardShadow,
  padding: { xs: "28px 24px 24px", sm: "40px 44px 36px" },
  boxSizing: "border-box",
} as const;

/**
 * Thin bridge page -- mirrors ss-mailzzy-web's McpAuthorize exactly. It does
 * NOT render its own login form: an unauthenticated (or stale-token) visitor
 * bounces to the real Login screen with `?redirectionUrl=` so MFA, SSO,
 * Google, and password-expiry all go through the app's one real login path
 * and land back here (see authSession.ts/Login.tsx/OTP.tsx/SetupMFA.tsx).
 * Once authenticated, this page's only job is a real top-level form POST of
 * the access token to /mcp/auth-bridge.
 */
const McpAuthorize = () => {
  useTitle(title.MCP_AUTHORIZE);
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const accessToken = useAppSelector(selectAccessToken);
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    // A persisted `authenticated` flag can outlive the access token, so
    // posting a stale token to /mcp/auth-bridge gets it rejected as an
    // expired JWT -- check the token's own expiry, not just redux state.
    // jwtDecode, not atob: JWT segments are base64url, and atob throws on
    // '-'/'_', which made a valid token look expired.
    const isExpired = (jwt?: string | null) => {
      if (!jwt) return true;
      try {
        const { exp } = jwtDecode<{ exp?: number }>(jwt);
        return !exp || exp * 1000 <= Date.now() + 30_000; // 30s clock skew
      } catch {
        return true;
      }
    };

    const redirectToLogin = () => {
      const encodedUrl = encodeURIComponent(viewpaths.mcpAuthorizeViewPath);
      history.replace(`${viewpaths.loginViewPath}?redirectionUrl=${encodedUrl}`);
    };

    if (!isLoggedIn || !accessToken) {
      redirectToLogin();
      return;
    }
    if (isExpired(accessToken)) {
      // Clear the stale session BEFORE redirecting, or `authenticated` stays
      // true and the login page immediately honors redirectionUrl and
      // bounces straight back here with the same expired token -- an
      // infinite /login <-> /mcp-authorize loop. Logging out first forces
      // the login page to render its form so a fresh token gets minted.
      dispatch(logOutAction({}));
      redirectToLogin();
      return;
    }
    formRef.current?.submit();
  }, [isLoggedIn, accessToken, dispatch]);

  return (
    <AuthLayout>
      <Box>
        <Box
          aria-hidden="true"
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: signerAccentBar,
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        />
        <Card elevation={0} sx={cardSx}>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box
                component="img"
                src={Logo}
                alt="Inkfree e-Sign"
                sx={{ width: 106, height: "auto", display: "block" }}
              />
              <Typography
                component="h1"
                role="status"
                sx={{
                  color: mobile.base[500],
                  fontSize: refFont.cardHeading,
                  fontWeight: 700,
                  textAlign: "center",
                  mt: 3,
                }}
              >
                Connecting your Inkfree account
              </Typography>
              <Typography
                sx={{
                  color: authRef.subText,
                  fontSize: refFont.cardBody,
                  lineHeight: 1.6,
                  textAlign: "center",
                  mt: 1,
                }}
              >
                One moment while we take you back to the application you are
                connecting.
              </Typography>

              <LinearProgress
                sx={{
                  width: "100%",
                  mt: 3,
                  height: 6,
                  borderRadius: "999px",
                  backgroundColor: authRef.inputBorder,
                  "& .MuiLinearProgress-bar": {
                    borderRadius: "999px",
                    backgroundColor: signer.orange.main,
                  },
                }}
              />

              <Typography
                sx={{
                  mt: 2,
                  fontSize: refFont.label,
                  color: authRef.metaText,
                  textAlign: "center",
                }}
              >
                Your credentials are never shared with the application.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <form
          ref={formRef}
          method="POST"
          action={urls.MCP_AUTH_BRIDGE}
          style={{ display: "none" }}
        >
          <input type="hidden" name="token" value={accessToken ?? ""} />
        </form>
      </Box>
    </AuthLayout>
  );
};

export default McpAuthorize;
